import React, { useEffect, useRef, useState } from 'react';
import { Col, InputGroupAddon } from 'reactstrap';
import { Input, Date, Select, Checkbox } from '../../lib/form-field';
import { useTranslation } from 'react-i18next';
import useClipboard from '../../lib/useClipboard';

import moment from 'moment';

const InputLead = ({
  listValues,
  typeField,
  onChangeUpdateField,
  asyncUrlValues,
  children,
  ...restProps
}) => {
  const { t } = useTranslation();
  switch (typeField) {
    case 'select':
      return (
        <Select
          isMulti={false}
          isSearchable={true}
          options={
            Array.isArray(listValues)
              ? listValues.map(({ title, value }) => ({
                  label: t(title),
                  value,
                }))
              : []
          }
          onChange={(event) => onChangeUpdateField(event)}
          {...restProps}
        >
          {children}
        </Select>
      );
    case 'async-select':
      const loadOptions = async (search, loadedOptions) => {
        if (search < 3) return { options: [] };

        //?search=&offset=0 || ?search=mu&offset=0
        const response = await fetch(
          `${asyncUrlValues}/?search=${encodeURIComponent(
            search.toLowerCase(),
          )}&offset=${loadedOptions.length}`,
        );
        const responseJSON = await response.json();

        return {
          options: responseJSON,
        };
      };

      return (
        <Select
          isMulti={false}
          isSearchable={true}
          loadOptions={loadOptions}
          debounceTimeout={500}
          loadOptionsOnMenuOpen={false}
          onChange={(event) => onChangeUpdateField(event)}
          {...restProps}
        >
          {children}
        </Select>
      );
    case 'date':
      return (
        <Date format="DD/MM/YYYY" {...restProps}>
          {children}
        </Date>
      );
    case 'time':
      return (
        <Input type="time" {...restProps}>
          {children}
        </Input>
      );
    case 'boolean':
      return <Checkbox {...restProps} />;
    case 'textarea':
      return (
        <Input type="textarea" {...restProps}>
          {children}
        </Input>
      );
    default:
      return <Input {...restProps}>{children}</Input>;
  }
};

const CopyToClipboardButton = ({ onCopyToClipboard }) => {
  const [status, setStatus] = useState('idle');
  const resetTimeout = useRef(null);

  useEffect(
    () => () => {
      clearTimeout(resetTimeout.current);
    },
    [],
  );

  const handleClick = async () => {
    const copied = await onCopyToClipboard();

    setStatus(copied ? 'copied' : 'failed');
    clearTimeout(resetTimeout.current);
    resetTimeout.current = setTimeout(() => setStatus('idle'), 1500);
  };

  const icon = {
    idle: 'bx-copy-alt',
    copied: 'bx-check text-success',
    failed: 'bx-x text-danger',
  }[status];

  return (
    <button
      className="btn btn-outline-dark p-0"
      style={{ width: '31px', zIndex: 0 }}
      type="button"
      onClick={handleClick}
    >
      <i className={`bx ${icon} d-block font-size-15`} />
    </button>
  );
};

const InputLeadGroup = (props) => (
  <Col md={props.size}>
    {props.onCopyToClipboard ? (
      <InputLead addonType="prepend" {...props}>
        <InputGroupAddon addonType="prepend">
          <CopyToClipboardButton onCopyToClipboard={props.onCopyToClipboard} />
        </InputGroupAddon>
      </InputLead>
    ) : (
      <InputLead {...props} />
    )}
  </Col>
);

const onChangeUpdateField = (field, setFieldValue) => {
  if (!field.onChangeUpdateKey) return () => {};

  //if place is city not update zipCode field
  if (field.onChangeUpdateField === 'zipCode')
    return (value) =>
      value.cityName !== value.place &&
      value[field.onChangeUpdateKey] &&
      setFieldValue(field.onChangeUpdateField, value[field.onChangeUpdateKey]);

  return (value) =>
    value[field.onChangeUpdateKey] &&
    setFieldValue(field.onChangeUpdateField, value[field.onChangeUpdateKey]);
};

const InputLeadField = ({ field, setFieldValue, values, ...restProps }) => {
  const [, copyToClipboard] = useClipboard();
  const { t } = useTranslation();

  // An empty field must still overwrite the clipboard: leaving the previous
  // content in place would let the operator paste the value copied before.
  const valueToCopy = (field, values) => {
    const value = values[field.name];
    if (value === null || value === undefined || value === '') return '';

    switch (field.typeField) {
      case 'select':
        return t(value);
      // The stored value is either the selected option or a plain scalar: an
      // option carrying neither label nor value must not end up in the
      // clipboard as "[object Object]".
      case 'async-select':
        if (typeof value !== 'object') return value;
        return value.label ?? value.value ?? '';
      case 'date':
        return moment(value).isValid()
          ? moment(value).format('DD/MM/YYYY')
          : '';
      default:
        return value;
    }
  };

  const handleCopyToClipboard = (field, values) =>
    copyToClipboard(valueToCopy(field, values));

  return (
    <InputLeadGroup
      key={field.id}
      name={field.name}
      typeField={field.typeField}
      listValues={field.listValues}
      asyncUrlValues={field.asyncUrlValues}
      size={field.size || '3'}
      required={field.required ?? false}
      readOnly={field.readOnly ?? false}
      onCopyToClipboard={() => handleCopyToClipboard(field, values)}
      onChangeUpdateField={onChangeUpdateField(field, setFieldValue)}
      {...restProps}
    />
  );
};

export default InputLeadField;
